import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  Provider,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, noop } from 'rxjs';
import {
  ControlChange,
  ControlIdentity,
  ControlParameters,
  ControlState,
  EntityFeaturePatchConflictResponse,
  EntityFeaturePatchRequest,
} from '../../../api/model/models';
import { Random } from '../../../utils';
import { Labeled, LabelPosition, ValidationProvider, ValidationSeverity } from '../shared';
import { CplaceControlService } from './cplace-control.service';

export const CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR: (component: unknown) => Provider = (component: unknown) => ({
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => component),
  multi: true,
});

/**
 * Base cplace control component for creating controls specifically for cplace.
 */
@UntilDestroy()
@Component({
  template: '',
  encapsulation: ViewEncapsulation.None,
})
export abstract class CplaceControlComponent<T> implements Labeled, ValidationProvider, ControlValueAccessor {
  /**
   * Informational alert shown with the label.
   */
  @Input() public alert = '';

  /**
   * Whether the cplace control is required or not.
   */
  @Input() public required = false;

  /**
   * Whether the cplace control is readonly.
   */
  @Input() public readonly = false;

  /**
   * cplace control label.
   */
  @Input() public set label(val: string) {
    this.$label.next(val);
  }

  public get label(): string {
    return this.$label.getValue();
  }

  public $label: BehaviorSubject<string> = new BehaviorSubject<string>('');

  /**
   * cplace control label position.
   */
  @Input() public labelPosition: LabelPosition = 'top';

  /**
   * cplace control validation message.
   */
  @Input() public validationMessage = '';

  /**
   * cplace control validation severity.
   */
  @Input() public validationSeverity: ValidationSeverity = 'info';

  /**
   * Disables and greys out the cplace control.
   */
  @Input() public disabled = false;

  /**
   * The control's associated identity.
   */
  @Input()
  public identity?: ControlIdentity;

  /**
   * Gets the value of the control.
   */
  public get value(): T | undefined {
    return this.$value.getValue();
  }

  /**
   * Set accessor including call to the onchange callback.
   * @param value The new value for the element.
   */
  @Input() public set value(value: T | undefined) {
    if (value !== this.value) {
      this.$value.next(value);
      if (this.onChangeCallback) {
        this.onChangeCallback(value);
      }
    }
  }

  /**
   * Value of the cplace control.
   */
  public $value: BehaviorSubject<T | undefined> = new BehaviorSubject<T | undefined>(undefined);

  /**
   * Tells whether the cplace control has been interacted with in any way.
   * @private
   */
  @HostBinding('attr.aria-pressed') private touched = false;

  /**
   * Used to link the label and the native form component.
   */
  public readonly nativeFormComponentId = `cf-frontend-sdk-native-form-component-${Random.generateId()}`;

  /**
   * Used to identify a specific instance of this component.
   */
  public readonly componentId = `cf-frontend-sdk-component-id-${Random.generateId()}`;

  /**
   * Whether the cplace control should be shown in submitting state.
   */
  public submitting = false;

  /**
   * Used for {@link ControlValueAccessor}.
   * @private
   */
  private onTouchedCallback?: () => void;

  /**
   * Used for {@link ControlValueAccessor}.
   * @private
   */
  private onChangeCallback?: (_: unknown) => void;

  /**
   * All cplace controls have a reference to their host element.
   * @param elementRef Host element reference.
   * @param changeDetectorRef Change detector reference.
   * @param cplaceControlService Control service.
   */
  protected constructor(
    public elementRef: ElementRef<HTMLElement>,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {}

  /**
   * Called when either the save button or keyboard shortcut is used to save the control.
   */
  public onSubmit() {
    this.beforeSubmitHook();

    if (!this.identity) {
      throw new Error('The control identity is undefined');
    }

    this.submitting = true;

    const entityFeaturePatchRequest: EntityFeaturePatchRequest = {
      entityUid: this.identity.entityUid,
      changes: [this.getControlChange(this.identity, this.value)],
    };

    this.cplaceControlService
      .submit(entityFeaturePatchRequest)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: ControlState[] | EntityFeaturePatchConflictResponse) => {
          if (Array.isArray(response) && response.length > 0) {
            this.initializeControlState(response[0]);
            this.afterSubmitHook();
          } else {
            const controlChangeConflictResponse = response as EntityFeaturePatchConflictResponse;
            if (controlChangeConflictResponse.conflicts.length > 0) {
              this.validationMessage = controlChangeConflictResponse.conflicts[0].validationMessage;
              this.validationSeverity = 'warning';
            }
          }

          this.submitting = false;
          this.changeDetectorRef.detectChanges();
        },
        (error) => {
          console.error(
            `(${this.getComponentClassName()}) [entity=${this.identity?.entityUid}, feature=${
              this.identity?.featureName
            }] Error submitting control:`,
            error
          );

          this.submitting = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  /**
   * Used for {@link ControlValueAccessor}.
   * @param fn onChange callback.
   */
  public registerOnChange(fn: (_: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  /**
   * Used for {@link ControlValueAccessor}.
   * @param fn onTouched callback.
   */
  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  /**
   * Used for {@link ControlValueAccessor}.
   * @param isDisabled The disabled status to set on the element.
   */
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Used for {@link ControlValueAccessor}.
   * @param value The new value for the element.
   */
  public writeValue(value: T | undefined): void {
    if (value !== this.value) {
      this.$value.next(value);
    }
  }

  /**
   * Initializes the cplace control with the control state received from the server.
   * @param controlState Control state.
   */
  public initializeControlState(controlState: ControlState) {
    this.label = controlState.label;
    this.alert = controlState.alert ? controlState.alert : '';
    this.validationMessage = controlState.validationMessage ? controlState.validationMessage : '';
    this.required = controlState.isRequired;
    this.readonly = controlState.readonly;
    this.identity = controlState.identity;
    this.initializeControlParameters(controlState.controlParameters);
  }

  /**
   * Initializes the control's parameters. This should set any internal variables specific to the control.
   * @param controlParameters Control parameters.
   * @protected
   */
  protected abstract initializeControlParameters(controlParameters: ControlParameters): void;

  /**
   * Creates and returns a control change when submitting data.
   * @protected
   */
  protected abstract getControlChange(identity: ControlIdentity, value: T | undefined): ControlChange;

  /**
   * Returns the component class name.
   * @protected
   */
  protected abstract getComponentClassName(): string;

  /**
   * Should implement logic that is needed before submitting the control.
   * @protected
   */
  protected beforeSubmitHook(): void {
    noop();
  }

  /**
   * Should implement logic that is needed after submitting the control.
   * @protected
   */
  protected afterSubmitHook(): void {
    noop();
  }

  /**
   * The control is marked as touched when the control is focused into.
   * @private
   */
  @HostListener('focusin')
  private markAsTouched() {
    if (!this.touched) {
      if (this.onTouchedCallback) {
        this.onTouchedCallback();
      }
      this.touched = true;
    }
  }
}
